import { useState } from 'react';
import { UPGRADE_DEFS } from '../game/constants';
import { formatNumber, formatRate } from '../game/utils';
import { getUpgradeCost, getBulkUpgradeCost, getMaxAffordableCount } from '../game/useGameState';

type BuyQty = 1 | 10 | 100 | 'max';

const QTY_OPTIONS: { label: string; value: BuyQty }[] = [
  { label: '×1', value: 1 },
  { label: '×10', value: 10 },
  { label: '×100', value: 100 },
  { label: '×Max', value: 'max' },
];

interface UpgradePanelProps {
  energy: number;
  ownedUpgrades: Record<string, number>;
  currentEraIndex: number;
  onBuy: (id: string) => void;
  onBuyBulk: (id: string, qty: number | 'max') => void;
}

export function UpgradePanel({
  energy,
  ownedUpgrades,
  currentEraIndex,
  onBuy,
  onBuyBulk,
}: UpgradePanelProps) {
  const [buyQty, setBuyQty] = useState<BuyQty>(1);
  const visible = UPGRADE_DEFS.filter((u) => u.minEraIndex <= currentEraIndex);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Upgrades</h3>
        <div className="flex items-center gap-1">
          {QTY_OPTIONS.map(({ label, value }) => (
            <button
              key={label}
              onClick={() => setBuyQty(value)}
              className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                buyQty === value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex max-h-[520px] flex-col gap-2 overflow-y-auto pr-1">
        {visible.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10">
            <span className="text-3xl opacity-40">🔒</span>
            <p className="text-center text-sm text-gray-500">
              Keep clicking to unlock upgrades...
            </p>
          </div>
        ) : (
          visible.map((def) => {
            const owned = ownedUpgrades[def.id] ?? 0;
            const singleCost = getUpgradeCost(def.id, owned);
            const maxCount = buyQty === 'max' ? getMaxAffordableCount(def.id, owned, energy) : 0;

            const displayQty = buyQty === 'max' ? maxCount : buyQty;
            const displayCost = displayQty === 0 ? singleCost : getBulkUpgradeCost(def.id, owned, displayQty);
            const canAfford = buyQty === 'max' ? maxCount > 0 : energy >= singleCost;
            const totalProduction = def.baseProduction * owned;

            return (
              <button
                key={def.id}
                onClick={() => (buyQty === 1 ? onBuy(def.id) : onBuyBulk(def.id, buyQty))}
                disabled={!canAfford}
                className={`group w-full rounded-xl border p-3 text-left transition-all duration-150 ${
                  canAfford
                    ? 'cursor-pointer border-gray-600/80 bg-gray-800/80 shadow-sm hover:border-blue-500/50 hover:bg-gray-700/80 hover:shadow-md hover:shadow-blue-900/20'
                    : 'cursor-not-allowed border-gray-800/60 bg-gray-900/40'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className={`shrink-0 text-xl transition-opacity ${canAfford ? 'opacity-100' : 'opacity-40'}`}
                      role="img"
                      aria-hidden="true"
                    >
                      {def.icon}
                    </span>
                    <div className="min-w-0">
                      <div className={`truncate text-sm font-medium transition-colors ${canAfford ? 'text-white' : 'text-gray-500'}`}>
                        {def.name}
                      </div>
                      <div className="truncate text-xs text-gray-500">{def.description}</div>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div
                      className={`text-sm font-bold tabular-nums ${
                        canAfford ? 'text-yellow-400' : 'text-gray-600'
                      }`}
                    >
                      ⚡ {formatNumber(displayCost)}
                    </div>
                    {owned > 0 && (
                      <div className="text-xs text-blue-400/80 tabular-nums">
                        ×{owned} · +{formatRate(totalProduction)}/s
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
